import { CourseUnit } from '@/graphql/generated/types';
import { DiscountLabel } from '@/order/discount-label/discount-label.component';
import globalStyles from '@/styles/global.scss';
import { Chip } from '@/ui/chip/chip.component';
import { Formatted } from '@/ui/formatted/formatted.component';
import { Row } from '@/ui/table/row.component';
import { Table } from '@/ui/table/table.component';
import { Link } from '@reach/router';
import React, { ComponentType, useMemo } from 'react';
import {
  getCourseUnitDates,
  getCourseUnitLocationLabel,
} from 'shared/course/utils';
import { OrdersQuery } from '../../list/list.generated';
import styles from './order-items-overview.module.scss';

type OrderItem = OrdersQuery['adminOrders']['data'][number]['items'][number];

function getItemTaxRate(item: OrderItem): number {
  if (
    item.__typename === 'BookingOrderItem' ||
    item.__typename === 'ProductOrderItem' ||
    item.__typename === 'PackageBookingOrderItem'
  ) {
    return item.taxRate ?? 0;
  }
  return 0;
}

function getItemDisplayNetAmount(item: OrderItem): boolean {
  if (item.__typename === 'BookingOrderItem') {
    const booking = item.booking as any;
    return (
      booking?.courseUnit?.course?.displayNetAmount ||
      booking?.courseModule?.courseUnit?.course?.displayNetAmount ||
      false
    );
  }
  if (item.__typename === 'ProductOrderItem') {
    return (item.product as any)?.displayNetAmount ?? false;
  }
  if (item.__typename === 'PackageBookingOrderItem') {
    return item.package.displayNetAmount;
  }
  return false;
}

interface OrderItemsOverviewProps {
  order: OrdersQuery['adminOrders']['data'][number];
}

function getProductName(
  item: OrdersQuery['adminOrders']['data'][number]['items'][number],
) {
  if (item.__typename === 'BookingOrderItem') {
    const booking = item.booking as any;
    const locationLabel = getCourseUnitLocationLabel(
      booking.courseModule || booking.courseUnit,
    );

    return (
      <>
        {booking.courseModule?.courseUnit.course.abbreviation ||
          booking.courseUnit?.course.abbreviation}
        {locationLabel && <> ({locationLabel})</>}
      </>
    );
  }

  if (item.__typename === 'PackageBookingOrderItem') {
    return (
      <>
        {item.package.title} - {item.package.subTitle}
      </>
    );
  }

  if (item.__typename === 'ProductOrderItem') {
    return item.product.title;
  }

  return 'Rabatt';
}

export const OrderItemsOverview: ComponentType<OrderItemsOverviewProps> = (
  props,
) => {
  const { order } = props;

  const items = useMemo(() => {
    return order.items.filter((i) => i.__typename !== 'DiscountOrderItem');
  }, [order.items]);

  // amount is always net; taxByRate stores net line totals grouped by rate
  const taxByRate = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of items) {
      const rate = getItemTaxRate(item);
      if (rate > 0 && getItemDisplayNetAmount(item)) {
        map.set(rate, (map.get(rate) ?? 0) + item.amount * item.quantity);
      }
    }
    return map;
  }, [items]);

  const netTotal = useMemo(
    () =>
      items
        .filter((item) => getItemDisplayNetAmount(item))
        .reduce((sum, item) => sum + item.amount * item.quantity, 0),
    [items],
  );

  const hasTaxes = taxByRate.size > 0;

  const grossTotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const rate = getItemTaxRate(item);
      return sum + item.amount * (1 + rate) * item.quantity;
    }, 0);
  }, [items]);

  return (
    <Table
      compact
      columns={[
        'Anzahl',
        'Produkt',
        'Unternehmen',
        'Herkunft',
        <th key="price" className="right">Preis</th>,
        <th key="vat" className="right">MwSt.</th>,
      ]}
      data={items}
      additionalRows={
        <>
          {order.discounts.map((d) => (
            <Row key={d.id} className={styles.withBorderBottom}>
              <td></td>
              <td>{d.reason}</td>
              <td></td>
              <td></td>
              <td className="numeric">
                <DiscountLabel type={d.type} value={d.amount} />
              </td>
              <td></td>
            </Row>
          ))}
          {hasTaxes && (
            <>
              <Row>
                <td colSpan={2}></td>
                <td>Netto</td>
                <td></td>
                <td className="numeric">
                  <Formatted.Currency value={netTotal} />
                </td>
                <td></td>
              </Row>
              {Array.from(taxByRate.entries()).map(([rate, netAmount]) => (
                <Row key={rate}>
                  <td colSpan={2}></td>
                  <td>MwSt. {Math.round(rate * 100)} %</td>
                  <td></td>
                  <td className="numeric">
                    <Formatted.Currency value={netAmount * rate} />
                  </td>
                  <td></td>
                </Row>
              ))}
            </>
          )}
          <Row>
            <td colSpan={2}></td>
            <td className="total">Gesamt</td>
            <td></td>
            <td className="numeric total">
              <Formatted.Currency value={grossTotal} />
            </td>
            <td></td>
          </Row>
        </>
      }
      render={(item) => {
        let enterpriseName = '';
        let booking;
        let courseUnitItem: CourseUnit | undefined = undefined;
        if (item.__typename === 'ProductOrderItem') {
          enterpriseName = item.product.enterprise.name;
        } else if (item.__typename === 'BookingOrderItem') {
          booking = item.booking;

          enterpriseName =
            booking?.courseUnit?.course.category.enterprise.name ||
            booking?.courseModule?.courseUnit?.course.category.enterprise.name;
          courseUnitItem = booking?.courseUnit || booking?.courseModule;
        } else if (item.__typename === 'PackageBookingOrderItem') {
          enterpriseName = item.package.enterprise.name;
        }

        const innerProduct = (
          <>
            {getProductName(item)}
            {courseUnitItem && (
              <div className={globalStyles.sub}>
                {getCourseUnitDates(courseUnitItem)}
              </div>
            )}
          </>
        );

        const taxRate = getItemTaxRate(item);
        const displayNet = getItemDisplayNetAmount(item);
        // amount is net; show gross (net × (1+rate)) unless displayNetAmount=true
        const displayPrice =
          !displayNet && taxRate > 0
            ? item.amount * (1 + taxRate)
            : item.amount;

        return (
          <>
            <Row
              className={
                (!item.discounts || item.discounts.length == 0) &&
                styles.withBorderBottom
              }
            >
              <td>{item.quantity}</td>
              <td>
                {courseUnitItem && (
                  <Link to={`/course-handling/bookings/${booking?.id}/view`}>
                    {innerProduct}
                  </Link>
                )}
                {!courseUnitItem && innerProduct}
              </td>
              <td>{enterpriseName}</td>
              <td>{item.source && <Chip>{item.source}</Chip>}</td>
              <td className="numeric">
                <Formatted.Currency value={displayPrice} />
              </td>
              <td className="numeric">
                {taxRate > 0
                  ? displayNet
                    ? `zzgl. ${Math.round(taxRate * 100)} %`
                    : `inkl. ${Math.round(taxRate * 100)} %`
                  : ''}
              </td>
            </Row>
            {item.discounts?.length > 0 && (
              <>
                {item.discounts.map((d, i) => (
                  <Row
                    className={
                      i === item.discounts.length - 1 && styles.withBorderBottom
                    }
                    key={d.id}
                  >
                    <td>{item.quantity}</td>
                    <td>{d.reason}</td>
                    <td>{enterpriseName}</td>
                    <td></td>
                    <td className="numeric">
                      <DiscountLabel value={d.amount} type={d.type} />
                    </td>
                    <td></td>
                  </Row>
                ))}
              </>
            )}
          </>
        );
      }}
    />
  );
};
